import React, { useEffect, useRef, useState } from "react";
import { getAllUsers, getUsers } from "apis/adminServices/userManagmentService";
import BlockUserModal from "./blockUserModal";
import { useGlobalLoader } from "contexts/GlobalLoaderContext";
import { useToastStore } from "store/toastStore";
import { OgUserGroup, User } from "./type";
import UserFilterDropdown from "./userFilterDropdown";
import MoveToModal from "./moveToModal";
import { moveUsersToOgGroup } from "./ogGroupActions";
import RowsPerPageSelect from "components/admin/RowsPerPageSelect";
import CreditTokenModal from "./creditTokenModal";
import UserDetailsModal from "./userDetailsModal";
import { hasLifetimeOgRole } from "utils/roles";

interface UsersListProps {
  /** Every OG user group, for the "Move to" targets. */
  groups: OgUserGroup[];
  /** Reloads the roles and the tab counts after users change group. */
  onMembershipChange?: () => void;
}

const UsersList: React.FC<UsersListProps> = ({ groups, onMembershipChange }) => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalUsers, setTotalUsers] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState("");
  const { showLoader, hideLoader } = useGlobalLoader();
  const { showToast } = useToastStore();
  const [isFilterOpen, setIsFilterOpen] = useState<boolean>(false);
  const [isMoveToOpen, setIsMoveToOpen] = useState<boolean>(false);
  const [moving, setMoving] = useState<boolean>(false);

  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [selectedUsers, setSelectedUsers] = useState<User[]>([]);
  const [isCreditModalOpen, setIsCreditModalOpen] = useState(false);
  const [detailsUser, setDetailsUser] = useState<User | null>(null);
  const hasLoadedOnce = useRef(false);

  const fetchUsers = async (
    page: number,
    size: number,
    showGlobalLoader = true
  ) => {
    try {
      setLoading(true);
      if (showGlobalLoader) showLoader();
      const response = await getUsers(searchTerm, filter, page, size);
      // No roleId: this tab lists every user, OG members included — they show
      // up here as well as on their own tab.
      setUsers(getAllUsers(response));
      setTotalUsers(response?.totalCount ?? 0);
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      hideLoader();
      setLoading(false);
    }
  };

  const handleFilterChange = (filters: { status?: string }) => {
    setFilter(filters.status || "");
    setCurrentPage(1);
    setSelectedUsers([]);
    setIsFilterOpen(false);
  };

  const handleMoveToClick = () => {
    if (selectedUsers.length === 0) {
      showToast("Please select at least one user to move.", "error", 3000);
      return;
    }
    const blockedUsers = selectedUsers.filter(
      (u) => u.status?.toLowerCase() === "blocked"
    );
    if (blockedUsers.length > 0) {
      if (blockedUsers.length === 1 && selectedUsers.length === 1) {
        showToast(
          "Blocked users cannot be moved to another user role.",
          "error",
          3000
        );
      } else {
        showToast(
          "Blocked users cannot be moved to another user role. Please deselect blocked user(s) to continue.",
          "error",
          3000
        );
      }
      return;
    }
    setIsMoveToOpen(true);
  };

  const handleMoveTo = async (roleId: number | null) => {
    const blockedUsers = selectedUsers.filter(
      (u) => u.status?.toLowerCase() === "blocked"
    );
    if (blockedUsers.length > 0) {
      showToast(
        "Blocked users cannot be moved to another user role.",
        "error",
        3000
      );
      return;
    }
    setMoving(true);
    const result = await moveUsersToOgGroup(selectedUsers, roleId, groups);
    setMoving(false);
    showToast(
      result.message,
      result.toastType || (result.failed > 0 ? "error" : "success"),
      3000
    );

    setIsMoveToOpen(false);
    setSelectedUsers([]);
    if (result.succeeded > 0) {
      // The moved users keep their row here, but their token balance changed.
      fetchUsers(currentPage, pageSize, false);
      onMembershipChange?.();
    }
  };

  const isUserUnlimited = (user?: User | null): boolean => {
    if (!user) return false;
    return (
      hasLifetimeOgRole(user.userRoles) ||
      user.totalCredits === null ||
      user.totalCredits === undefined
    );
  };

  const handleCreditTokenClick = () => {
    if (selectedUsers.length === 0) {
      showToast(
        "Please select at least one user to credit the token.",
        "error",
        3000
      );
      return;
    }

    const unlimitedUsers = selectedUsers.filter(isUserUnlimited);
    if (unlimitedUsers.length > 0) {
      if (unlimitedUsers.length === selectedUsers.length) {
        showToast(
          "Cannot credit tokens. Selected user(s) already have unlimited tokens.",
          "error",
          3000
        );
        return;
      }
      showToast(
        "User(s) with unlimited tokens cannot receive additional credits and were excluded.",
        "error",
        3000
      );
      setSelectedUsers((prev) => prev.filter((u) => !isUserUnlimited(u)));
    }

    setIsCreditModalOpen(true);
  };

  const toggleUserSelection = (user: User) => {
    setSelectedUsers((prev) =>
      prev.some((u) => u.userId === user.userId)
        ? prev.filter((u) => u.userId !== user.userId)
        : [...prev, user]
    );
  };

  const allOnPageSelected =
    users.length > 0 &&
    users.every((user) =>
      selectedUsers.some((selected) => selected.userId === user.userId)
    );

  const togglePageSelection = () => {
    if (allOnPageSelected) {
      setSelectedUsers((prev) =>
        prev.filter((selected) => !users.some((u) => u.userId === selected.userId))
      );
    } else {
      setSelectedUsers((prev) => [
        ...prev,
        ...users.filter(
          (user) => !prev.some((selected) => selected.userId === user.userId)
        ),
      ]);
    }
  };

  useEffect(() => {
    const showGlobalLoaderForThisFetch = !hasLoadedOnce.current;
    hasLoadedOnce.current = true;
    fetchUsers(currentPage, pageSize, showGlobalLoaderForThisFetch);
  }, [currentPage, pageSize, searchTerm, filter]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
    setSelectedUsers([]);
  };

  const openBlockUserModal = (user: User) => {
    setSelectedUser(user);
  };

  const closeBlockUserModal = () => {
    setSelectedUser(null);
  };

  const totalPages = Math.max(1, Math.ceil(totalUsers / pageSize));

  const generatePageNumbers = () => {
    const pages: number[] = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    const endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

    if (endPage === totalPages) {
      startPage = Math.max(1, totalPages - maxPagesToShow + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return pages;
  };

  return (
    <section>
      <div className="bg-white rounded-xl p-4 mt-2">
        <div className="flex flex-wrap gap-4 items-center justify-end pb-4">
          <div className="flex flex-wrap items-center gap-3 relative">
            <div className="relative">
              <input
                className="block w-full p-3 pl-10 pr-10 rounded-lg bg-lightblue-100 text-sm text-grease-100 placeholder-gray-400 focus:outline-none"
                placeholder="Search by keyword"
                value={searchTerm}
                onChange={handleSearch}
              />
              <button className="absolute w-fit inset-y-0 start-0 flex items-center z-20 px-3 cursor-pointer text-gray-400 rounded-e-md focus:outline-none">
                <i className="st-search text-lightgray-200"></i>
              </button>
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm("")}
                  aria-label="Clear search"
                  className="absolute inset-y-0 end-0 flex items-center z-30 px-3 text-gray-400 hover:text-gray-600 focus:outline-none"
                >
                  <svg className="w-3 h-3" viewBox="0 0 14 14" fill="none">
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
                    />
                  </svg>
                </button>
              )}
            </div>
            <button
              className="flex items-center justify-center gap-2 p-3 rounded-lg bg-[#FFBC00] hover:opacity-90 transition-opacity"
              onClick={handleMoveToClick}
            >
              <p className="font-medium text-white text-sm">Move to</p>
            </button>

            <button
              className="flex items-center justify-center gap-2 p-3 rounded-lg bg-[#FFBC00]"
              onClick={handleCreditTokenClick}
            >
              <p className="font-medium text-white text-sm">
                Credit Token
                {selectedUsers.length > 0 ? ` (${selectedUsers.length})` : ""}
              </p>
            </button>

            <div className="relative">
              <button
                id="dropdownDefaultButton-3"
                data-dropdown-toggle="dropdown-3"
                className="flex items-center justify-center gap-2 p-3 bg-[#FFBC00] hover:opacity-90 transition-opacity rounded-lg"
                onClick={() => setIsFilterOpen(true)}
              >
                <i className="st-filter text-white"></i>
                <p className="font-medium text-white text-sm">Filters</p>
              </button>

              <UserFilterDropdown
                onClose={() => setIsFilterOpen(false)}
                onFilterChange={handleFilterChange}
                isOpen={isFilterOpen}
              />
            </div>
          </div>
        </div>

        <div className="relative overflow-x-auto overflow-y-auto max-h-[calc(100vh-280px)] min-h-[300px] sm:rounded-lg">
          <table className="w-full text-sm text-left text-gray-500">
            <thead className="text-xs text-gray-700 bg-blue-50 sticky top-0 z-10 shadow-sm whitespace-nowrap">
              <tr>
                <th scope="col" className="pl-6 pr-2 py-4 rounded-l-lg w-10 sticky top-0 bg-blue-50 z-10 whitespace-nowrap">
                  <input
                    type="checkbox"
                    aria-label="Select all users on this page"
                    checked={allOnPageSelected}
                    disabled={loading || users.length === 0}
                    onChange={togglePageSelection}
                    className="accent-primaryblue-100 cursor-pointer"
                  />
                </th>
                <th scope="col" className="px-6 py-4 sticky top-0 bg-blue-50 z-10 whitespace-nowrap">
                  <p className="text-lightgray-200 whitespace-nowrap">User ID</p>
                </th>
                <th scope="col" className="px-6 py-4 sticky top-0 bg-blue-50 z-10">
                  <p className="border-l-2 border-lightgray-50 text-lightgray-200 px-4">
                    User Name
                  </p>
                </th>
                <th scope="col" className="px-6 py-4 sticky top-0 bg-blue-50 z-10">
                  <p className="border-l-2 border-lightgray-50 text-lightgray-200 px-4">
                    Joined Date
                  </p>
                </th>
                <th scope="col" className="px-6 py-4 sticky top-0 bg-blue-50 z-10">
                  <p className="border-l-2 border-lightgray-50 text-lightgray-200 px-4">
                    Email Address
                  </p>
                </th>
                <th scope="col" className="px-6 py-4 sticky top-0 bg-blue-50 z-10">
                  <p className="border-l-2 border-lightgray-50 text-lightgray-200 px-4">
                    Status
                  </p>
                </th>
                <th scope="col" className="px-6 py-4 sticky top-0 bg-blue-50 z-10">
                  <p className="border-l-2 border-lightgray-50 text-lightgray-200 px-4">
                    Total Tokens
                  </p>
                </th>
                <th scope="col" className="px-6 py-4 sticky top-0 bg-blue-50 z-10"></th>
              </tr>
            </thead>
            <tbody className="rounded-lg">
              {loading ? (
                <tr>
                  <td colSpan={8} className="text-center py-4">
                    Loading...
                  </td>
                </tr>
              ) : users && users.length > 0 ? (
                users.map((user) => (
                  <tr
                    key={user?.userId}
                    className="odd:bg-white even:bg-blue-50 border"
                  >
                    <td className="pl-6 pr-2 py-4 w-10">
                      <input
                        type="checkbox"
                        aria-label={`Select user ${user?.userId}`}
                        checked={selectedUsers.some(
                          (selected) => selected.userId === user?.userId
                        )}
                        onChange={() => toggleUserSelection(user)}
                        className="accent-primaryblue-100 cursor-pointer"
                      />
                    </td>
                    <td className="px-6 py-4 font-normal text-sm text-grease-100 whitespace-nowrap">
                      <p>{user?.userId}</p>
                    </td>
                    <td className="px-6 py-4 font-normal text-sm text-grease-100 whitespace-nowrap">
                      <p className="px-4">
                        {user.username
                          ? `${user.username.charAt(0).toUpperCase()}${user.username.slice(1)}`
                          : "UserName"}
                      </p>
                    </td>
                    <td className="px-6 py-4 font-normal text-sm text-grease-100 whitespace-nowrap">
                      <p className="px-4">
                        {new Date(user?.joinedDate).toLocaleDateString("en-GB")}
                      </p>
                    </td>
                    <td className="px-6 py-4 font-normal text-sm text-grease-100 whitespace-nowrap">
                      <p className="px-4">{user?.email}</p>
                    </td>
                    <td className="px-6 py-4 font-normal text-grease-100 whitespace-nowrap">
                      <p
                        className={`mx-4 px-3 py-1 text-white text-xs rounded-full w-fit ${
                          user?.status === "Active" ? "bg-teal-500" : "bg-red-500"
                        }`}
                      >
                        {user?.status || "unknown"}
                      </p>
                    </td>
                    <td className="px-6 py-4 font-normal text-sm text-grease-100 whitespace-nowrap">
                      <p className="px-4">
                        {hasLifetimeOgRole(user?.userRoles)
                          ? "Unlimited"
                          : user?.totalCredits}
                      </p>
                    </td>
                    <td className="px-6 py-4 font-normal text-sm text-grease-100 whitespace-nowrap">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => setDetailsUser(user)}
                          className="rounded-md px-4 py-2 text-xs w-20 text-primaryblue-100 border border-primaryblue-100"
                        >
                          Details
                        </button>
                        <button
                          onClick={() => openBlockUserModal(user)}
                          className={`
                            rounded-md px-4 py-2 text-xs w-20
                            ${
                              user?.status === "Blocked"
                                ? "text-red-500 border border-red-500"
                                : "text-teal-500 border border-teal-500"
                            }
                          `}
                        >
                          {user?.status === "Blocked" ? "Unblock" : "Block"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={8} className="text-center py-24 text-lightgray-200 text-sm">
                    No users found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {totalUsers > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-4 mt-4">
            <div className="flex flex-wrap items-center gap-5">
              <p className="text-lightgray-200 text-sm">
                Total {totalUsers} {totalUsers === 1 ? "record" : "records"}
              </p>
              <p className="text-lightgray-200 text-sm">Rows per page</p>
              <RowsPerPageSelect
                value={pageSize}
                onChange={(size) => {
                  setPageSize(size);
                  setCurrentPage(1);
                  setSelectedUsers([]);
                }}
              />
            </div>

            <div>
              <ul className="flex flex-wrap items-center justify-center my-6 gap-3 text-sm">
                <li>
                  <button
                    disabled={currentPage === 1}
                    className={`flex items-center justify-center w-7 h-7 leading-tight border rounded-md ${
                      currentPage === 1
                        ? "text-gray-300 border-gray-300"
                        : "text-primaryblue-50 border-primaryblue-50"
                    }`}
                    onClick={() => setCurrentPage(currentPage - 1)}
                  >
                    <span className="sr-only">Previous</span>
                    <i className="st-arrow-right text-xl font-semibold rotate-180"></i>
                  </button>
                </li>

                {generatePageNumbers().map((pageNum) => (
                  <li key={pageNum}>
                    <button
                      className={`flex items-center justify-center w-7 h-7 rounded-md ${
                        currentPage === pageNum
                          ? "text-white border border-primaryblue-50 bg-primaryblue-50"
                          : "text-primaryblue-50 border border-primaryblue-50"
                      }`}
                      onClick={() => setCurrentPage(pageNum)}
                    >
                      {pageNum}
                    </button>
                  </li>
                ))}

                <li>
                  <button
                    disabled={currentPage === totalPages}
                    className={`flex items-center justify-center w-7 h-7 leading-tight border rounded-md ${
                      currentPage === totalPages
                        ? "text-gray-300 border-gray-300"
                        : "text-primaryblue-50 border-primaryblue-50"
                    }`}
                    onClick={() => setCurrentPage(currentPage + 1)}
                  >
                    <span className="sr-only">Next</span>
                    <i className="st-arrow-right text-xl font-semibold"></i>
                  </button>
                </li>
              </ul>
            </div>
          </div>
        )}
      </div>

      <UserDetailsModal
        isOpen={detailsUser !== null}
        user={detailsUser}
        onClose={() => setDetailsUser(null)}
      />

      <MoveToModal
        isOpen={isMoveToOpen}
        onClose={() => setIsMoveToOpen(false)}
        groups={groups}
        currentRoleId={null}
        onMove={handleMoveTo}
        moving={moving}
      />

      <CreditTokenModal
        isOpen={isCreditModalOpen}
        users={selectedUsers}
        onClose={() => setIsCreditModalOpen(false)}
        onCredited={() => {
          setSelectedUsers([]);
          fetchUsers(currentPage, pageSize);
        }}
      />

      {selectedUser && (
        <BlockUserModal
          userDetails={selectedUser}
          onClose={closeBlockUserModal}
          onBlockSuccess={() => {
            fetchUsers(currentPage, pageSize);
          }}
        />
      )}
    </section>
  );
};

export default UsersList;
